/**********************************************************/
/* DooFinder Live Layer integration ***********************/
/**********************************************************/
/*
 * Three jobs here:
 *   1. Keep .dfd-layer.dfd-ext positioned directly under #header, even as
 *      the header height changes (logged-in admin bar, resize, etc.).
 *   2. Inject an "Are you looking for…" category pills block into the
 *      layer when it mounts. The pill data is emitted by the theme into
 *      a <script id="df-category-pill-data"> tag (see
 *      inc/_doofinder-integration.php).
 *   3. Hydrate the four placeholder containers each result card carries
 *      (FBT pills, coupon pills, Trustpilot widget, wishlist button) by
 *      fetching matching HTML fragments from the wp-json acity/v1 routes
 *      defined in inc/_doofinder-hydration.php. Run multibuy-count refresh
 *      after hydration so the .multi-qty badge reflects the real cookie
 *      state instead of the static "2" baked into the indexed template.
 *
 * DooFinder mounts the layer into <body> asynchronously after its own
 * JS boots, then mutates the results panel as the user types. We use a
 * MutationObserver to catch both the initial mount and any subsequent
 * results re-render, so newly inserted cards always get hydrated.
 */

const $ = require('jquery');

const HYDRATION_ENDPOINTS = ['fbt-pills', 'coupon-pills', 'trustpilot', 'wishlist-button'];

module.exports = {
  init_doofinder: function () {
    const LAYER_SELECTOR = '.dfd-layer.dfd-ext';
    const HEADER_SELECTOR = '#header';

    /* -- 0. Disable DooFinder infinite-scroll sentinel on mobile -------
     *
     * Root cause of the typing flicker: DooFinder ships a
     * `[data-dfd-role="scroll-sentinel"]` element inside `.dfd-content`
     * which their LiveView client watches with an IntersectionObserver
     * to drive infinite scroll (`load-more` hook events).
     *
     * On desktop the sentinel sits below the vertically-stacked results
     * grid and only intersects the viewport once the user has actually
     * scrolled to the bottom. On mobile, our customisation reshapes the
     * results grid into a HORIZONTAL scroll carousel
     * (`.acity-results-carousel`) — a single row of cards roughly the
     * height of one card. The sentinel ends up sitting just under that
     * row, *inside* the visible viewport from the moment results render.
     *
     * That puts the load-more loop into permanent retrigger:
     *   1. Sentinel is visible → IntersectionObserver fires.
     *   2. DooFinder pushes `load-more` to the LiveView server.
     *   3. Server streams one more card into `.dfd-results-grid` (~30ms).
     *   4. The horizontal row grows wider but the sentinel still sits in
     *      viewport (the row is still only one card tall).
     *   5. Each stream patch triggers morphdom, which reverts the inline
     *      style / data-attrs our injection passes set on
     *      `.dfd-smart-filters`, and our MutationObserver fires the
     *      seven-function hydrate pass to put them back. The visible
     *      result is the search panel "flickering" as the user types,
     *      with network traffic running at ~20 messages/second even
     *      when the user has stopped typing.
     *
     * Killing the sentinel via CSS is the cleanest fix — morphdom never
     * touches a `<style>` tag we own, and DooFinder's intersection
     * observer simply never fires because the sentinel's computed
     * `display` is `none`. The user still sees the full first page of
     * results (10 cards) and can scroll the horizontal carousel; we
     * just don't keep asking the server for more. The desktop /
     * non-mobile experience is untouched because the selector is
     * scoped to `.dfd-root.dfd-mobile`.
     */
    (function injectMobileSentinelOverride() {
      const STYLE_ID = 'acity-doofinder-mobile-overrides';
      if (document.getElementById(STYLE_ID)) return;
      const style = document.createElement('style');
      style.id = STYLE_ID;
      style.textContent =
        '.dfd-root.dfd-mobile .dfd-content [data-dfd-role="scroll-sentinel"],' +
        '.dfd-root.dfd-mobile .dfd-content [data-dfd-role="scroll-top-sentinel"]{' +
          'display:none !important;' +
        '}';
      (document.head || document.documentElement).appendChild(style);
    })();

    /* -- 1. Header-height sync ---------------------------------------
     *
     * DooFinder mounts `.dfd-root.dfd-fullscreen` as `position: fixed;
     * inset: 0;` — it covers the entire viewport including the header,
     * so clicks on the search input go to the layer's backdrop instead
     * of the input itself. We expose the header height as a CSS custom
     * property (`--ac-df-header-offset`) on <html>; `_doofinder.scss`
     * consumes it via `top: var(--ac-df-header-offset)` on both the
     * fullscreen root and the inner layer, pushing the whole overlay
     * below the header and leaving the header interactive.
     *
     * We set it on every mount and on every resize — admin bar, sticky
     * header promos, mobile → desktop transitions all change the header
     * height dynamically.
     */

    function syncLayerTop() {
      const headerHeight = $(HEADER_SELECTOR).outerHeight() || 0;

      // The DooFinder layer always starts at `.top-navigation-container`'s
      // bottom edge — across every breakpoint:
      //
      //   - Desktop (xxl+): form is inline inside the top-nav row, so
      //     top-nav.bottom is naturally just below the form, with the
      //     mega-menu nav row overlaid by the layer beneath.
      //   - Tablet (991–1439): form is `position: fixed; top: 0` — it
      //     overlays the top-nav row, so the layer continues from
      //     where the original top-nav ended (visually identical to
      //     the desktop case).
      //   - Mobile (≤990): the open search form is `position: fixed;
      //     top: 0` and visually sits ABOVE the original top-nav row,
      //     so we need to use the form's bottom as the layer top —
      //     `top-navigation-container.bottom` would put the layer
      //     behind the form input. Falls back to the top-nav bottom
      //     when the mobile form isn't open.
      const openMobileForm = document.querySelector('.search-form-container--mobile.is-open');
      const topNavEl = document.querySelector('.top-navigation-container');
      // Only use the mobile form's bottom when it's actually rendered.
      // On desktop the `.search-form-container--mobile` element is still
      // in the DOM (and DooFinder's fullscreen URL hash flips `.is-open`
      // on it as a side-effect), but it's hidden by CSS — so its
      // bounding rect is 0×0 and `bottom` returns 0. Without this
      // visibility check, syncLayerTop would set the CSS var to
      // `0 - 1 = -1px` and the entire overlay would render at the top
      // of the viewport instead of below the header.
      const openMobileFormRect = openMobileForm ? openMobileForm.getBoundingClientRect() : null;
      const mobileFormIsVisible = openMobileFormRect && openMobileFormRect.height > 0 && openMobileFormRect.bottom > 0;
      let layerTop;
      if (mobileFormIsVisible) {
        layerTop = Math.round(openMobileFormRect.bottom);
      } else if (topNavEl) {
        layerTop = Math.round(topNavEl.getBoundingClientRect().bottom);
      } else {
        layerTop = headerHeight;
      }

      // Two CSS variables:
      //   --ac-df-header-offset-base : full header height (consumed by
      //     legacy mobile rules — kept for backwards-compat even though
      //     the current `.is-open` rule pins to `top: 0`).
      //   --ac-df-header-offset      : where the DooFinder layer should
      //     start (top edge), used by
      //     `.dfd-root.dfd-fullscreen { top: var(...) }`.
      document.documentElement.style.setProperty(
        '--ac-df-header-offset-base',
        headerHeight + 'px'
      );
      // -1px to overlap the top-nav's bottom border by a single pixel,
      // closing a sub-pixel rounding gap that's otherwise visible as a
      // thin horizontal page-background sliver between the top-nav
      // and the layer chrome at some breakpoints.
      document.documentElement.style.setProperty(
        '--ac-df-header-offset',
        (layerTop - 1) + 'px'
      );
    }

    // Expose so scripts.js can call it after toggling the form.
    window.ac_syncLayerTop = syncLayerTop;

    syncLayerTop();
    let resizeTimer;
    $(window).on('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(syncLayerTop, 100);
    });

    // Visual viewport listener — fires when the iOS / Android soft
    // keyboard opens or closes (and on URL-bar shrink, page zoom,
    // pinch zoom). `window.innerHeight` and CSS `dvh` units don't
    // both shrink reliably mid-page for the keyboard case, so the
    // mobile layer ends up sized to the full viewport while the
    // keyboard covers its bottom half, hiding suggestions / results
    // behind the keyboard. Re-running `syncLayerTop` propagates the
    // new offset to `--ac-df-header-offset`, and `sizeMobileRootToContent`
    // re-runs from the existing resize binding below to clamp height.
    //
    // Resize only — the `scroll` event on visualViewport fires on every
    // layout shift caused by DooFinder typing/rendering, and since
    // `syncLayerTop` writes CSS vars to `<html>`, each call triggers
    // another layout shift and another scroll event, producing a tight
    // ~0.3s feedback flicker as the user types. Resize alone covers
    // keyboard show/hide which is the only case we actually need.
    //
    // Module-scope flag prevents binding twice if init runs again
    // (defensive — current code only inits once per page load).
    if (window.visualViewport && !window.__acDfViewportBound) {
      window.visualViewport.addEventListener('resize', syncLayerTop);
      window.__acDfViewportBound = true;
    }

    /* -- Open the search form whenever the layer is in fullscreen mode
     *
     * DooFinder persists its open state in `location.hash` (e.g.
     * `#4e6a/fullscreen/m=and&q=beko`) and rehydrates the layer on
     * reload — but our `.search-form-container.is-open` flag isn't
     * persisted, so the user lands on a page with the layer showing
     * search results behind the layer chrome and no visible input to
     * refine the query.
     *
     * Watch `.dfd-root` for the `dfd-fullscreen` class. When present,
     * mirror it onto the search-form container so the input shows
     * above the layer.
     */
    let acPendingCloseMirror = null;
    function syncSearchFormWithLayer() {
      // When the user has just toggled the form closed via the
      // `.search-btn` / `#search-toggle` handler in scripts.js, the
      // DooFinder layer doesn't drop `dfd-fullscreen` synchronously
      // — its close animation runs over a few frames. Without this
      // suppression, the MutationObserver below would re-run this
      // function during the close animation, see `layerOpen=true` +
      // `formOpen=false`, and incorrectly re-add `.is-open`, which
      // makes the second toggle click feel like a no-op until the
      // user blurs first.
      if (window.ac_searchFormUserClosing) return;

      // Mirror counterpart for the OPEN side of the same race. On a
      // cold first tap of `.search-btn`, the toggle handler adds
      // `.is-open` to the form and pokes DooFinder's input — but
      // `.dfd-root` may take several frames (and class flips along
      // the way) to land `dfd-fullscreen`. Without this guard the
      // observer fires during the mount, sees `layerOpen=false +
      // formOpen=true`, and strips `.is-open` — which is exactly why
      // the first tap appeared to do nothing and the second tap was
      // needed to "open" the search.
      if (window.ac_searchFormUserOpening) return;

      const root = document.querySelector('.dfd-root');
      const $container = $('.search-form-container');
      if (!root || !$container.length) return;

      // Desktop opens via `.dfd-fullscreen`; mobile uses `.dfd-mobile`
      // and never adds the fullscreen class, so a fullscreen-only check
      // mis-reads mobile as "closed" the instant the layer mounts and
      // strips `.is-open` from the form below — meaning the user's
      // first tap on `.search-btn` LOOKS like nothing happened (the
      // search input vanished as soon as it appeared). Treat presence
      // of `.dfd-mobile` + a rendered `.dfd-layer` as the open state
      // too, so the mobile search input stays visible above the layer.
      const layerEl = root.querySelector('.dfd-layer');
      // Mobile open-state must NOT rely on `offsetParent`/rendered height.
      // The customised mobile layer legitimately collapses to (near) zero
      // height in `mobile-initial`, and a fixed-position layer reports
      // `offsetParent === null` mid-mount — both made the old check read
      // the layer as "closed" while the form was still open, firing the
      // teardown branch below during Doofinder's own screen transitions
      // (initial -> typing -> results). That rips out the dialog a11y /
      // inert / pointer-events wiring and leaves the panel visible but
      // dead to taps (Beckie round 3). Treat a mounted, not-display:none
      // layer as open regardless of its transient rendered size.
      const layerHidden = !layerEl ||
        getComputedStyle(layerEl).display === 'none' ||
        getComputedStyle(root).display === 'none';
      const layerOpen =
        root.classList.contains('dfd-fullscreen') ||
        (root.classList.contains('dfd-mobile') && !layerHidden);
      const formOpen = $container.hasClass('is-open');

      // The layer is genuinely open right now — cancel any pending
      // close-mirror that an earlier render-blip may have scheduled.
      if (layerOpen) { clearTimeout(acPendingCloseMirror); acPendingCloseMirror = null; }

      if (layerOpen && !formOpen) {
        // Layer opened by something other than the toggle (e.g. URL
        // hash on reload, or the value-poke in scripts.js) — mirror
        // it to the form so the input is visible above the layer.
        $container.addClass('is-open');
        // Lock body scroll for this open-side mirror too — the layer
        // is now visible regardless of which path opened it. The
        // helper is idempotent, so a subsequent toggle-handler open
        // won't double-snapshot.
        if (typeof window.ac_lockBodyScroll === 'function') {
          window.ac_lockBodyScroll();
        }
        // Resync immediately so the layer's `top` accounts for the
        // newly-revealed form's height.
        requestAnimationFrame(syncLayerTop);
        setTimeout(syncLayerTop, 120);
      } else if (!layerOpen && formOpen) {
        // Doofinder's LiveView transiently flips `.dfd-root` to
        // display:none during its own render/diff cycle, then re-shows it
        // a frame later (see the dflayer.min.js `hide()` -> applyDiff path
        // in the stack). Reacting to that momentary hide is what fired the
        // destructive teardown mid-render and left the panel visible but
        // dead to taps (Beckie round 3). So DON'T tear down on the spot —
        // debounce, and only mirror the close if the layer is STILL gone a
        // beat later. A genuine close stays closed; a render-blip recovers.
        clearTimeout(acPendingCloseMirror);
        acPendingCloseMirror = setTimeout(function () {
          acPendingCloseMirror = null;
          const $c = $('.search-form-container');
          if (!$c.hasClass('is-open')) return; // form already closed elsewhere

          const r = document.querySelector('.dfd-root');
          const lEl = r && r.querySelector('.dfd-layer');
          const hidden = !r || !lEl ||
            getComputedStyle(r).display === 'none' ||
            getComputedStyle(lEl).display === 'none';
          const stillOpen = !!r && (
            r.classList.contains('dfd-fullscreen') ||
            (r.classList.contains('dfd-mobile') && !hidden));

          if (stillOpen) return; // render blip — the layer is back, leave it

          // Sustained close (DooFinder's own close button, backdrop tap or
          // Esc). Mirror it back to the form, then tear down a11y wiring
          // before unlock/history so focus restores to the trigger.
          $c.removeClass('is-open');
          if (typeof window.ac_teardownDoofinderDialog === 'function') {
            window.ac_teardownDoofinderDialog();
          }
          if (typeof window.ac_unlockBodyScroll === 'function') {
            window.ac_unlockBodyScroll();
          }
          if (typeof window.ac_replaceDoofinderHistoryIfPushed === 'function') {
            window.ac_replaceDoofinderHistoryIfPushed();
          }
          requestAnimationFrame(syncLayerTop);
          setTimeout(syncLayerTop, 120);
        }, 250);
      }
    }

    // First check on init (covers reload-with-hash). The layer mounts
    // async after DooFinder's script boots, so we also retry briefly
    // and rely on the MutationObserver below for later state changes.
    syncSearchFormWithLayer();
    setTimeout(syncSearchFormWithLayer, 250);
    setTimeout(syncSearchFormWithLayer, 750);
    setTimeout(syncSearchFormWithLayer, 1500);

    // Long-lived observer: watches `.dfd-root` for class changes so we
    // catch the `dfd-fullscreen` toggle as DooFinder opens / closes.
    // Set up via MutationObserver on the body — when `.dfd-root` is
    // first inserted we grab it, then watch its `class` attribute.
    let rootObserved = false;
    function observeRootClass(rootEl) {
      if (rootObserved || !rootEl) return;
      rootObserved = true;
      new MutationObserver(syncSearchFormWithLayer).observe(rootEl, {
        attributes: true,
        attributeFilter: ['class'],
      });
      // Also fire once now in case the class is already correct.
      syncSearchFormWithLayer();
    }

    const existingRoot = document.querySelector('.dfd-root');
    if (existingRoot) observeRootClass(existingRoot);

    new MutationObserver(function (mutations) {
      if (rootObserved) return;
      for (const m of mutations) {
        for (const node of m.addedNodes) {
          if (node.nodeType !== 1) continue;
          if (node.classList && node.classList.contains('dfd-root')) {
            observeRootClass(node);
            return;
          }
          const inner = node.querySelector ? node.querySelector('.dfd-root') : null;
          if (inner) { observeRootClass(inner); return; }
        }
      }
    }).observe(document.body, { childList: true, subtree: true });

    /* -- 1b. Mirror header search → DooFinder layer search input ------
     *
     * DooFinder's layer ships with its OWN search input inside
     * `.dfd-searchbox` (rendered into `.dfd-header`). We hide that input
     * via SCSS so users only ever see the theme's header search, BUT
     * DooFinder's internal state machine (the LiveView component that
     * runs the query, swaps initial-content for results, fetches
     * suggestions etc.) only listens to events fired on its own input —
     * typing in our header input does nothing on its own.
     *
     * Fix: every `input` on `#df-search`, find DooFinder's input,
     * write our value to it, then dispatch `input` + `change` events
     * via the native value setter so framework-bound listeners (the
     * layer is a Phoenix LiveView component) actually fire.
     *
     * Without this the layer is permanently stuck in initial-content
     * mode regardless of what the user types.
     */

    // Use the prototype's native `value` setter so framework listeners
    // (React/LiveView/etc.) see the change. A plain `el.value = x` is
    // bypassed by some frameworks because they patch the setter.
    function setNativeInputValue(el, value) {
      const proto = Object.getPrototypeOf(el);
      const desc  = Object.getOwnPropertyDescriptor(proto, 'value');
      if (desc && typeof desc.set === 'function') {
        desc.set.call(el, value);
      } else {
        el.value = value;
      }
    }

    function findDoofinderInput() {
      // The layer's input lives inside `.dfd-searchbox` (or sometimes
      // `.dfd-search` depending on layer version). Try a few selectors
      // so we're not pinned to a specific class name.
      return document.querySelector(
        '.dfd-searchbox input, .dfd-searchbox__input, ' +
        '.dfd-search input, .dfd-header input[type="search"], ' +
        '.dfd-header input[type="text"]'
      );
    }

    function mirrorSearchInput(value) {
      const target = findDoofinderInput();
      if (!target) return false;
      if (target.value === value) return true; // already in sync, skip
      setNativeInputValue(target, value);
      target.dispatchEvent(new Event('input', { bubbles: true }));
      target.dispatchEvent(new Event('change', { bubbles: true }));
      return true;
    }

    // Bind to ALL `.df-search-input` elements (class, not ID) so both
    // the desktop and mobile copies of the searchform forward keystrokes
    // to DooFinder's hidden internal input. They share a class but
    // have unique IDs (`df-search` / `df-search-mobile`) — see
    // searchform.php's `variant` arg.
    //
    // Two-way sync: when the user types in one input (e.g. mobile),
    // also update the other (desktop) so the visible value stays
    // consistent if the user resizes mid-search.
    const $headerSearch = $('.df-search-input');
    if ($headerSearch.length) {
      $headerSearch.on('input', function () {
        mirrorSearchInput(this.value);
        // Keep the other copy in sync without re-firing the input event
        $headerSearch.not(this).each(function () {
          if (this.value !== $headerSearch.first().val) this.value = $headerSearch.first()[0].value;
        });
        // Simpler: just propagate THIS value to all siblings
        const v = this.value;
        $headerSearch.not(this).each(function () { if (this.value !== v) this.value = v; });
      });

      // Enter inside the visible input → submit the form to
      // `/shop/?s={query}` for a full WC archive search.
      //
      // Previously we forwarded Enter to DooFinder's hidden input so
      // it could handle the submit "in-layer". DooFinder's listener
      // would `preventDefault()` to keep search in the live panel,
      // which silently swallowed the user's submit and never reached
      // the shop archive — exactly the opposite of what we want.
      //
      // Capture phase + `stopImmediatePropagation` guarantees we win
      // over DooFinder's bubble-phase listener regardless of which
      // script binds first. We then trigger the form's native submit
      // so the browser navigates per the `action` attribute.
      document.addEventListener('keydown', function (e) {
        if (e.key !== 'Enter' && e.keyCode !== 13) return;
        const t = e.target;
        if (!t || !t.classList || !t.classList.contains('df-search-input')) return;

        e.preventDefault();
        e.stopImmediatePropagation();
        const form = t.form || t.closest('form.df-search-form');
        if (form) form.submit();
      }, true);

      // Layer mounts asynchronously after our init runs, so the input
      // may not exist yet when the user has already typed. Re-mirror
      // once the layer is in the DOM (handled in `onLayerReady` below).
    }

    /* -- 2. "Are you looking for…" category pills ---------------------
     *
     * Injects a bespoke block of top-level product-category pills above
     * DooFinder's native Recommended Products section (which we relabel
     * as "Our Favourites" purely in CSS — see _doofinder.scss). The
     * category data is emitted inline into a
     * <script id="df-category-pill-data"> tag by
     * `inc/_doofinder-integration.php`.
     *
     * Classes are `.ac-doofinder-categories*` so the block is styled
     * entirely by our SCSS without depending on Bootstrap / theme
     * utility classes (those get overridden inside the layer).
     */

    let categoryData = null;
    try {
      const raw = document.getElementById('df-category-pill-data');
      if (raw && raw.textContent) {
        categoryData = JSON.parse(raw.textContent);
      }
    } catch (e) {
      categoryData = null; // fail quiet — layer still works
    }

    function htmlEscape(str) {
      return $('<div>').text(str == null ? '' : String(str)).html();
    }

    // Build a `.dfd-bc-aside` block in DooFinder's native HTML shape
    // so it slots cleanly into `.dfd-initial-content` and inherits all
    // the existing pill styling. Each entry is rendered as an `<a>`
    // (rather than DooFinder's default `<button dfd-click="next-screen">`)
    // — the curated list is a set of marketing-controlled category
    // shortcuts, so clicking them navigates straight to the category
    // archive page rather than triggering DooFinder's filter system.
    // Anchor form also gives the user middle-click / right-click /
    // open-in-new-tab behaviour that buttons can't.
    //
    // The category data comes from `_doofinder-integration.php` →
    // `ac_doofinder_get_category_pill_data()`.
    function buildBcAsideMarkup(cats) {
      if (!cats || !cats.length) return '';

      // Mobile: cap the pill list at 5 so the wrap layout stays compact
      // (per client brief). Desktop keeps the full curated list.
      // `(max-width: 991.98px)` matches the `@include lg` Bootstrap
      // breakpoint used everywhere else in the theme.
      var visible = cats;
      if ( window.matchMedia && window.matchMedia('(max-width: 991.98px)').matches ) {
        visible = cats.slice(0, 5);
      }

      const links = visible
        .map(function (c) {
          const href = c.url ? encodeURI(c.url) : '#';
          return (
            '<div class="dfd-suggestion-link">' +
              '<a href="' + href + '">' + htmlEscape(c.name) + '</a>' +
            '</div>'
          );
        })
        .join('');

      return (
        '<div class="dfd-bc-aside">' +
          '<div>' +
            '<div class="dfd-title-lg">Categories</div>' +
            links +
          '</div>' +
        '</div>'
      );
    }

    // Mount our pre-built `.dfd-bc-aside` block inside the layer's
    // `.dfd-initial-content` so the "Are you looking for…" row appears
    // immediately on layer mount — DooFinder lazy-renders its own
    // bc-aside, leaving the slot empty otherwise.
    //
    // Once DooFinder's own JS later writes its native bc-aside into
    // .dfd-initial-content (e.g. after first index hit), the DOM swap
    // replaces ours and we leave it alone — the short-circuit here
    // prevents us from re-injecting on every observer tick after that.
    function injectCategoriesInto($layer) {
      if (!categoryData || !$layer.length) return;

      const $initial = $layer.find('.dfd-initial-content').first();
      if (!$initial.length) return;

      // Already a bc-aside present (ours or DooFinder's native one).
      if ($initial.find('.dfd-bc-aside').length) return;

      const markup = buildBcAsideMarkup(categoryData);
      if (!markup) return;

      $initial.prepend(markup);
    }

    /* -- bc-aside button → archive URL navigation -------------------- *
     *
     * After the user types, DooFinder renders each suggestion in the
     * bc-aside as:
     *
     *   <button type="button"
     *           dfd-click="next-screen"
     *           dfd-value-query="beko"
     *           dfd-value-filter="Freestanding Fridge Freezers"
     *           dfd-value-field="categories">
     *     Freestanding Fridge Freezers
     *   </button>
     *
     * Default click runs DooFinder's `next-screen` action — drilling
     * into a sub-panel inside the layer instead of taking the user to
     * a real archive page. We intercept the click, look up the real
     * term archive URL (handles Yoast strips, WC permalink overrides,
     * custom rewrites like `/sinks-taps/`), and navigate.
     *
     * Why URL maps instead of `?product_cat=...&s=...`:
     *   - `/shop/?s=beko&product_cat=...` was returning all "beko"
     *     results across every category — the `product_cat` query var
     *     gets dropped by the search context on the shop page.
     *   - The category archive URL (e.g. `/freestanding-fridge-freezers/`)
     *     sets `tax_query` natively, then `?s=beko` refines within it.
     *
     * The map is emitted by `ac_doofinder_emit_term_url_map()` in
     * `inc/_doofinder-integration.php` keyed by lower-cased slug.
     *
     * Note attributes are bare `dfd-*` (no `data-` prefix), so we use
     * `getAttribute()` rather than `dataset`.
     */
    function slugifyBcAsideValue(text) {
      return String(text == null ? '' : text)
        .toLowerCase()
        .trim()
        .replace(/&amp;/g, '&')
        .replace(/&/g, 'and')
        .replace(/[^\w\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-');
    }

    // Cached parse of the URL-map JSON tag PHP emits. Returns
    // `{ categories: {slug: url}, brands: {slug: url} }` or empty
    // structure if the tag is missing.
    let bcAsideUrlMapCache = null;
    function getBcAsideUrlMap() {
      if (bcAsideUrlMapCache) return bcAsideUrlMapCache;
      const node = document.getElementById('df-term-url-map');
      if (!node) return { categories: {}, brands: {} };
      try {
        bcAsideUrlMapCache = JSON.parse(node.textContent || '{}');
      } catch (e) {
        bcAsideUrlMapCache = {};
      }
      bcAsideUrlMapCache.categories = bcAsideUrlMapCache.categories || {};
      bcAsideUrlMapCache.brands     = bcAsideUrlMapCache.brands     || {};
      return bcAsideUrlMapCache;
    }

    // On category archives the site filters by brand via `?brand={slug}`
    // (NOT `?s=` — full-text search broadens results across every
    // category and breaks the category context). Brand goes on the
    // query string raw-slugified so it matches the WCAPF / theme
    // brand-filter handler.
    function appendBrandFilter(url, brandSlug) {
      if (!brandSlug) return url;
      const sep = url.indexOf('?') !== -1 ? '&' : '?';
      return url + sep + 'brand=' + encodeURIComponent(brandSlug);
    }

    function buildBcAsideUrl(field, rawValue, query) {
      const slug = slugifyBcAsideValue(rawValue);
      if (!slug) return null;

      const maps = getBcAsideUrlMap();

      if (field === 'categories' || field === 'category') {
        // Real category archive URL — handles Yoast strips and any
        // custom permalink. If the user typed a query (usually a
        // brand name like "beko"), append it as `?brand={slug}` so
        // the category archive narrows to products of that brand.
        const archive = maps.categories[slug] || ('/product-category/' + slug + '/');
        const brandSlug = slugifyBcAsideValue(query);
        return brandSlug ? appendBrandFilter(archive, brandSlug) : archive;
      }

      if (field === 'brand' || field === 'brands') {
        // Brand archive URL, no refinement: clicking a brand
        // suggestion means "show me all of this brand", with WCAPF
        // sidebar available for further drill-down.
        const archive = maps.brands[slug];
        if (archive) return archive;
        // Fallback for unmapped brand slugs.
        return '/brand/' + slug + '/';
      }

      return null;
    }

    // Build the text we send to the server-side resolver from the
    // bc-aside button's `dfd-*` attributes. The resolver's precedence
    // ladder takes care of the brand+category / brand+attribute logic
    // we used to duplicate client-side in `buildBcAsideUrl`.
    //
    //   - categories field: query (a brand) + filter (the category) →
    //     "beko fridge freezers" → resolver hits the brand+category
    //     step and returns `/freestanding-fridge-freezers/?brand=beko`.
    //   - brand field: just the filter — the resolver matches it as
    //     an exact pa_brand term and returns the brand archive URL.
    //   - anything else: fall through to the local map so we don't
    //     break previously-working clicks if DooFinder adds a new
    //     field type we haven't taught the resolver about.
    function buildResolverInput(field, filter, query) {
      const f = String(filter || '').trim();
      const q = String(query || '').trim();
      if (!f && !q) return null;
      if (field === 'categories' || field === 'category') {
        return (q ? q + ' ' : '') + f;
      }
      if (field === 'brand' || field === 'brands') {
        return f || q;
      }
      // Unknown field — keep the literal filter text so the resolver
      // at least gets a chance to match it before we fall back.
      return f || q;
    }

    // REST endpoint URL (matches the route registered in
    // `inc/_doofinder-router.php` → `acity/v1/search-resolve`).
    const RESOLVER_ENDPOINT = '/wp-json/acity/v1/search-resolve';
    const RESOLVER_TIMEOUT_MS = 800;

    // Single round-trip to the resolver. Returns a promise that
    // resolves to a URL string when the server has one, or to null
    // when the call fails, times out, or the server reports a
    // fall-through. Callers should treat null as "use the local
    // fallback map instead".
    function resolveQueryViaRest(text) {
      if (!text) return Promise.resolve(null);

      // AbortController + a hard timeout — the bc-aside click is
      // already navigating away, so we can't let a slow API call
      // hold the click open. 800ms is well above the p99 for an
      // in-process resolver but short enough that the user doesn't
      // notice a stalled REST call.
      const ctrl = new AbortController();
      const timer = setTimeout(function () { ctrl.abort(); }, RESOLVER_TIMEOUT_MS);

      const url = new URL(RESOLVER_ENDPOINT, window.location.origin);
      url.searchParams.set('q', text);

      return fetch(url.toString(), {
        method: 'GET',
        credentials: 'same-origin',
        signal: ctrl.signal,
      })
        .then(function (res) {
          clearTimeout(timer);
          if (!res.ok) return null;
          return res.json();
        })
        .then(function (payload) {
          if (!payload || typeof payload.url !== 'string' || !payload.url) {
            return null;
          }
          return payload.url;
        })
        .catch(function () {
          clearTimeout(timer);
          return null;
        });
    }

    // Which suggestion pills we hijack. Two families:
    //   - `.dfd-bc-aside button[dfd-value-field]` — typed-state
    //     suggestions on desktop (original behaviour).
    //   - mobile `.dfd-smart-filters button[dfd-value-field]` — the
    //     typed-state pills on the `mobile-brand-categories` screen.
    //     Their default `next-screen` action drills into an in-layer
    //     filter panel; client wants them to land on the real archive
    //     page instead (Jul 2026), same as desktop suggestions. Desktop
    //     smart-filters keep DooFinder's native filtering.
    const PILL_BUTTON_SELECTOR =
      '.dfd-bc-aside button[dfd-value-field], ' +
      '.dfd-root.dfd-mobile .dfd-smart-filters button[dfd-value-field]';

    // Mobile-only: our injected "Categories" anchors on the initial
    // screen. Plain <a href> links, but the taps race LiveView stream
    // patches (recommended products streaming in re-render the parent
    // component) and the synthesized click gets lost — client repro:
    // pills dead on phones (Jul 2026). We navigate them ourselves.
    const PILL_ANCHOR_SELECTOR = '.dfd-root.dfd-mobile .dfd-bc-aside a[href]';

    // One tap can surface as touchend AND a synthesized click — the
    // lock makes whichever arrives second a no-op while a pill
    // navigation is already in flight.
    let pillNavStartedAt = 0;
    function pillNavLocked() {
      return pillNavStartedAt && (Date.now() - pillNavStartedAt) < 1500;
    }

    // Resolve a pill button to an archive URL and navigate. Returns
    // false (event untouched) when there's nothing we could navigate
    // to — DooFinder's own action stays in charge.
    //
    // Two-step resolve: the combined "query + filter" text first
    // (matches brand+category deals like "beko fridge freezers"),
    // then the filter alone. The query is whatever partial the user
    // typed ("Cookin"), which fails the combined resolve — and must
    // NOT reach the local fallback as a junk `?brand=cookin` param,
    // so smart-filter pills drop the query from the fallback URL too.
    function activatePillButton(btn, e) {
      const field  = btn.getAttribute('dfd-value-field');
      const filter = btn.getAttribute('dfd-value-filter') || (btn.textContent || '').trim();
      const query  = btn.getAttribute('dfd-value-query') || '';

      const isSmartFilter = !!btn.closest('.dfd-smart-filters');
      const localUrl = buildBcAsideUrl(field, filter, isSmartFilter ? '' : query);
      const combined = buildResolverInput(field, filter, query);

      if (!combined && !localUrl) {
        return false; // nothing to do — let DooFinder handle it
      }

      e.preventDefault();
      e.stopImmediatePropagation();
      pillNavStartedAt = Date.now();

      const filterOnly = buildResolverInput(field, filter, '');
      resolveQueryViaRest(combined)
        .then(function (restUrl) {
          if (restUrl) return restUrl;
          if (filterOnly && filterOnly !== combined) {
            return resolveQueryViaRest(filterOnly);
          }
          return null;
        })
        .then(function (restUrl) {
          const target = restUrl || localUrl;
          if (target) {
            window.location.href = target;
          } else {
            pillNavStartedAt = 0; // nothing resolved — release the lock
          }
        });
      return true;
    }

    // Capture phase — DooFinder's own `dfd-click` handler runs in
    // bubble phase on the same button. We need to intercept before it
    // calls preventDefault / runs `next-screen`, otherwise our redirect
    // gets cancelled mid-flight.
    document.addEventListener('click', function (e) {
      if (!(e.target && e.target.closest)) return;
      if (pillNavLocked()) {
        e.preventDefault();
        e.stopImmediatePropagation();
        return;
      }

      const anchor = e.target.closest(PILL_ANCHOR_SELECTOR);
      if (anchor && anchor.href) {
        // Force the navigation rather than trusting the anchor default
        // — the layer's mobile handlers can cancel it.
        e.preventDefault();
        e.stopImmediatePropagation();
        pillNavStartedAt = Date.now();
        window.location.href = anchor.href;
        return;
      }

      const btn = e.target.closest(PILL_BUTTON_SELECTOR);
      if (!btn) return;
      activatePillButton(btn, e);
    }, true);

    // Touch tap shim — on phones the tap's synthesized `click` can be
    // lost entirely when a LiveView stream patch re-renders the pill
    // between touchstart and touchend (constant while recommended
    // products stream in on layer open). Handle the tap at touchend
    // ourselves; movement/duration guards keep scrolls and long
    // presses out of it.
    let pillTouchStart = null;
    document.addEventListener('touchstart', function (e) {
      pillTouchStart = (e.touches && e.touches.length === 1)
        ? { x: e.touches[0].clientX, y: e.touches[0].clientY, at: Date.now() }
        : null;
    }, { capture: true, passive: true });

    document.addEventListener('touchend', function (e) {
      const start = pillTouchStart;
      pillTouchStart = null;
      if (!start || (Date.now() - start.at) > 700) return;
      const touch = e.changedTouches && e.changedTouches[0];
      if (!touch) return;
      if (Math.abs(touch.clientX - start.x) > 12 || Math.abs(touch.clientY - start.y) > 12) return;
      if (!(e.target && e.target.closest)) return;

      if (pillNavLocked()) {
        e.preventDefault();
        e.stopImmediatePropagation();
        return;
      }

      const anchor = e.target.closest(PILL_ANCHOR_SELECTOR);
      if (anchor && anchor.href) {
        e.preventDefault(); // also suppresses the synthesized click
        e.stopImmediatePropagation();
        pillNavStartedAt = Date.now();
        window.location.href = anchor.href;
        return;
      }

      const btn = e.target.closest(PILL_BUTTON_SELECTOR);
      if (btn && btn.closest('.dfd-root.dfd-mobile')) {
        activatePillButton(btn, e);
      }
    }, { capture: true, passive: false });

    /* -- 3. Card hydration (FBT, coupon, trustpilot, wishlist) ------- */

    // Pull cookie value by name. Mirrors the helper in js/multibuy.js so we
    // don't have to expose it as a public export — saves wiring up an inter-
    // module dependency for two lines of cookie parsing.
    function getCookie(name) {
      const value = '; ' + document.cookie;
      const parts = value.split('; ' + name + '=');
      if (parts.length === 2) return parts.pop().split(';').shift();
      return null;
    }

    function getMultibuyCount() {
      const cookie = getCookie('multibuy');
      if (!cookie) return 0;
      try {
        return JSON.parse(decodeURIComponent(cookie)).length;
      } catch (e) {
        return 0;
      }
    }

    // Idempotent — only writes if the existing text differs. Critical
    // because a blind .text() write triggers a childList mutation, which
    // re-fires our observer, which calls hydrateCards again. With the
    // equality check, the second pass is a no-op and the loop terminates.
    function refreshMultibuyQty(scope) {
      const text = String(getMultibuyCount());
      const nodes = scope.querySelectorAll('.multi-qty');
      for (let i = 0; i < nodes.length; i++) {
        if (nodes[i].textContent !== text) {
          nodes[i].textContent = text;
        }
      }
    }

    function hydrateFragment(el, endpoint, params) {
      el.dataset.hydrated = 'pending';

      const url = new URL(
        '/wp-json/acity/v1/doofinder/' + endpoint,
        window.location.origin
      );
      Object.keys(params).forEach(function (k) {
        if (params[k] !== undefined && params[k] !== null && params[k] !== '') {
          url.searchParams.set(k, params[k]);
        }
      });

      return fetch(url.toString(), { credentials: 'same-origin' })
        .then(function (res) {
          if (!res.ok) throw new Error('HTTP ' + res.status);
          return res.json();
        })
        .then(function (payload) {
          const html = payload && typeof payload.html === 'string' ? payload.html : '';
          el.innerHTML = html;
          el.dataset.hydrated = 'done';

          // Trustpilot's widget script scans on page load only; for elements
          // injected after that scan, we have to ask it to load this one.
          if (endpoint === 'trustpilot' && window.Trustpilot && html) {
            const widget = el.querySelector('.trustpilot-widget');
            if (widget) {
              try {
                window.Trustpilot.loadFromElement(widget, true);
              } catch (e) {
                // Trustpilot script not ready yet — it will scan on its own
                // initialise and pick this widget up.
              }
            }
          }
        })
        .catch(function (e) {
          el.dataset.hydrated = 'error';
          if (window.console && console.warn) {
            console.warn('[doofinder] Hydration failed for', endpoint, e);
          }
        });
    }

    // Per-card hydration is gated by a `data-acdf-hydrated` attribute on
    // the card root rather than by checking each placeholder's own dataset.
    // Reason: DooFinder mutates the layer subtree on hover, scroll, click
    // analytics, etc. Our observer fires on every mutation; without a
    // card-level token, hydrateCards re-walks every card every time and
    // mutations the multi-qty / inner HTML, which the observer sees as
    // more mutations — runaway loop. Card-level token + the inline
    // equality check inside refreshMultibuyQty stops it cold.
    function hydrateCards(root) {
      if (!root) return;
      // Match .ac-doofinder-card (current template) plus the legacy
      // .woocommerce-product-card class that earlier templates used,
      // so a stale template paste in DooFinder doesn't break hydration.
      const cards = root.querySelectorAll(
        '.ac-doofinder-card:not([data-acdf-hydrated]), .woocommerce-product-card:not([data-acdf-hydrated])'
      );
      if (!cards.length) {
        // Even when nothing new to hydrate, multi-qty may need a refresh
        // (e.g. user added an item to multibuy elsewhere). Idempotent —
        // no-op if values already match.
        refreshMultibuyQty(root);
        return;
      }

      cards.forEach(function (card) {
        card.setAttribute('data-acdf-hydrated', '1');

        // Product cards never render in the mobile layer (hidden via
        // _doofinder.scss, client brief Jul 2026: pills only) — don't
        // spend REST round-trips hydrating invisible cards.
        if (card.closest('.dfd-root.dfd-mobile')) return;

        const fbtEl = card.querySelector('.ac-doofinder-fbt-pills');
        if (fbtEl && !fbtEl.dataset.hydrated) {
          hydrateFragment(fbtEl, 'fbt-pills', {
            product_id: fbtEl.dataset.productId,
            banner_type: fbtEl.dataset.bannerType,
          });
        }

        const couponEl = card.querySelector('.ac-doofinder-coupon-pills');
        if (couponEl && !couponEl.dataset.hydrated) {
          hydrateFragment(couponEl, 'coupon-pills', {
            product_id: couponEl.dataset.productId,
            banner_type: couponEl.dataset.bannerType,
          });
        }

        const tpEl = card.querySelector('.ac-doofinder-trustpilot');
        if (tpEl && !tpEl.dataset.hydrated) {
          hydrateFragment(tpEl, 'trustpilot', { sku: tpEl.dataset.sku });
        }

        // Wishlist button removed from the search layer (Monday
        // pulses/12552238992): it didn't work in the DooFinder dropdown and
        // the client asked to remove it. Actively strip the placeholder from
        // every card rather than just leaving it un-hydrated — this covers
        // the DooFinder-cloud DESKTOP card template (which still injects the
        // placeholder) as well as the mobile rebuild, and does not depend on
        // the CSS bundle (which caches unreliably on this site).
        card.querySelectorAll('.ac-doofinder-wishlist, .ac-doofinder-card__wishlist').forEach(function (w) { w.remove(); });
      });

      refreshMultibuyQty(root);
    }

    /* -- Mobile dialog ARIA + focus trap -----------------------------
     *
     * The mobile layer is a modal — it covers the whole screen, blocks
     * interaction with the rest of the page (visually via the gradient
     * backdrop, logically via DooFinder's close-on-outside-tap behaviour).
     * Mark it up like a modal so screen reader and keyboard users get
     * the same experience:
     *
     *   1. `role="dialog"` + `aria-modal="true"` + `aria-label`
     *      describes the layer as a modal overlay.
     *   2. Focus moves to the visible search input on mount. Previously
     *      focused element is saved so we can restore on close.
     *   3. Tab / Shift+Tab cycle inside the "search-overlay" subtree —
     *      that's the union of `.search-form-container--mobile`
     *      (header search bar) and `.dfd-root.dfd-mobile` (layer body),
     *      because the visible input sits in the header outside the
     *      layer subtree.
     *   4. Everything OUTSIDE that subtree gets `inert` (or
     *      `aria-hidden="true"` on older Safari), so screen readers
     *      skip the page content while the layer is open.
     *
     * Idempotent — the dataset flag prevents double-setup if the layer
     * re-mounts and onLayerReady fires again.
     */
    let prevActiveElement = null;
    let mobileDialogKeyHandler = null;
    // Track the DOM nodes we marked `inert` / `aria-hidden` so close
    // can reverse exactly the same set even if siblings shifted.
    let inertedNodes = [];
    let ariaHiddenNodes = [];
    const FOCUSABLE_SELECTOR = [
      'a[href]',
      'button:not([disabled])',
      'input:not([type="hidden"]):not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ].join(',');

    // Feature check for the `inert` attribute. Supported in Chrome 102+,
    // Safari 15.5+, Firefox 112+. Older Safari (15.0–15.4) was the most
    // common UA we still see in the wild that lacks it, so fall back to
    // `aria-hidden="true"` for screen readers there (focus trapping
    // handles the keyboard side regardless).
    function supportsInert() {
      return 'inert' in HTMLElement.prototype;
    }

    function getOverlayRoots() {
      // Return the elements that make up the "search overlay" subtree
      // from the user's perspective. Includes the open mobile form
      // (where the visible input lives) and the DooFinder layer root.
      const roots = [];
      const form = document.querySelector('.search-form-container--mobile.is-open');
      if (form) roots.push(form);
      const dfRoot = document.querySelector('.dfd-root.dfd-mobile');
      if (dfRoot) roots.push(dfRoot);
      return roots;
    }

    function collectFocusables(roots) {
      const list = [];
      roots.forEach(function (root) {
        const found = root.querySelectorAll(FOCUSABLE_SELECTOR);
        for (let i = 0; i < found.length; i++) {
          // Skip hidden / 0-size nodes — they're focusable in spec but
          // user-hostile to land focus on. `offsetParent` is null for
          // any ancestor having `display: none`.
          const el = found[i];
          if (el.offsetParent === null && el.tagName !== 'BODY') continue;
          if (el.getAttribute('aria-hidden') === 'true') continue;
          list.push(el);
        }
      });
      return list;
    }

    function applyInertOrAriaHidden(node) {
      const supportsAttr = supportsInert();
      if (supportsAttr) {
        if (!node.inert) {
          node.inert = true;
          inertedNodes.push(node);
        }
      } else {
        if (node.getAttribute('aria-hidden') !== 'true') {
          node.setAttribute('aria-hidden', 'true');
          ariaHiddenNodes.push(node);
        }
      }
    }

    function inertSiblings(roots) {
      // Two passes:
      //
      //   1. Body-level siblings — inert anything at <body>'s direct
      //      child level that doesn't contain an overlay root. Catches
      //      <footer>, modals, compare-overlays, etc.
      //   2. Header chrome — the `<header id="header">` itself isn't
      //      inerted (it contains the open `.search-form-container--mobile`),
      //      but its non-search children ARE inerted so the nav,
      //      logo, basket / account icons can't grab Tab focus.
      //
      // Skip script / style nodes outright. Skip nodes that already
      // contain an overlay root (the search form lives inside the
      // header, so the header itself is in this set).
      const children = Array.from(document.body.children);
      children.forEach(function (node) {
        if (node.tagName === 'SCRIPT' || node.tagName === 'STYLE') return;
        const containsOverlay = roots.some(function (r) {
          return node === r || node.contains(r);
        });
        if (containsOverlay) {
          // Drill in one level: inert any child of this node that
          // doesn't itself contain an overlay root. This handles the
          // `<header>` case — header chrome (nav / logo / basket
          // icons) gets inerted, but the search form remains live.
          Array.from(node.children).forEach(function (sub) {
            if (sub.tagName === 'SCRIPT' || sub.tagName === 'STYLE') return;
            const subContains = roots.some(function (r) {
              return sub === r || sub.contains(r);
            });
            if (subContains) return;
            applyInertOrAriaHidden(sub);
          });
          return;
        }
        applyInertOrAriaHidden(node);
      });
    }

    function uninertSiblings() {
      inertedNodes.forEach(function (node) {
        try { node.inert = false; } catch (e) {}
      });
      inertedNodes = [];
      ariaHiddenNodes.forEach(function (node) {
        node.removeAttribute('aria-hidden');
      });
      ariaHiddenNodes = [];
    }

    function setupMobileDialogA11y($layer) {
      const layerEl = $layer && $layer.length ? $layer.get(0) : null;
      if (!layerEl) return;
      const root = layerEl.closest('.dfd-root');
      if (!root || !root.classList.contains('dfd-mobile')) return;
      // Idempotent — only run setup once per mount.
      if (root.dataset.acityDialogReady === '1') return;
      root.dataset.acityDialogReady = '1';

      // ARIA dialog semantics on the root. The layer itself is a
      // descendant — putting them on the root keeps the dialog
      // boundary aligned with the visual extent (root spans the
      // viewport; layer is the panel inside).
      root.setAttribute('role', 'dialog');
      root.setAttribute('aria-modal', 'true');
      root.setAttribute('aria-label', 'Search products');

      // Remember whatever the user had focused before opening, so we
      // can put focus back on close. Stored at module scope so it
      // survives re-renders inside the layer. If scripts.js has
      // already cached the pre-open active element (it does via
      // `window.__acDfPrevActive` set in the toggle handler before
      // the layer mounts), prefer that — the toggle button is a
      // more useful focus-restore target than whatever was focused
      // by the time the async-mounted layer settled.
      prevActiveElement = window.__acDfPrevActive || document.activeElement;
      delete window.__acDfPrevActive;

      // Hide the rest of the page from AT / keyboard.
      inertSiblings(getOverlayRoots());

      // Move focus to the visible search input — that's the user's
      // primary action. The input lives in the header form, not in
      // the layer itself, so we look outside the layer subtree.
      // `setTimeout` defers past the DooFinder mount frame where the
      // layer may steal focus to its own hidden input.
      setTimeout(function () {
        const visibleInput = document.querySelector(
          '.search-form-container--mobile.is-open .df-search-input'
        );
        if (visibleInput) {
          try { visibleInput.focus({ preventScroll: true }); } catch (e) { visibleInput.focus(); }
        }
      }, 0);

      // Focus trap. Capture-phase so it wins against DooFinder's own
      // keydown listeners (which intercept Tab to drive its filter UI
      // in some layouts).
      mobileDialogKeyHandler = function (e) {
        if (e.key !== 'Tab' && e.keyCode !== 9) return;
        const focusables = collectFocusables(getOverlayRoots());
        if (!focusables.length) return;
        const first = focusables[0];
        const last  = focusables[focusables.length - 1];
        const active = document.activeElement;

        if (e.shiftKey) {
          if (active === first || !getOverlayRoots().some(function (r) { return r.contains(active); })) {
            e.preventDefault();
            try { last.focus({ preventScroll: true }); } catch (err) { last.focus(); }
          }
        } else {
          if (active === last) {
            e.preventDefault();
            try { first.focus({ preventScroll: true }); } catch (err) { first.focus(); }
          }
        }
      };
      document.addEventListener('keydown', mobileDialogKeyHandler, true);
    }

    function teardownMobileDialogA11y() {
      const root = document.querySelector('.dfd-root.dfd-mobile');
      // Idempotency guard — if setup never ran (e.g. desktop) or
      // teardown already ran for this open cycle, bail out cleanly.
      // Multiple close-path call sites (toggle handler, delegated
      // close-button handler, observer mirror, popstate) all funnel
      // here, so duplicates are common and expected.
      if (!root || root.dataset.acityDialogReady !== '1') {
        // Defensive cleanup if setup was partial — clear flags and
        // any lingering inert markers so a follow-up open cycle
        // starts clean.
        if (mobileDialogKeyHandler) {
          document.removeEventListener('keydown', mobileDialogKeyHandler, true);
          mobileDialogKeyHandler = null;
        }
        uninertSiblings();
        return;
      }
      root.removeAttribute('role');
      root.removeAttribute('aria-modal');
      root.removeAttribute('aria-label');
      delete root.dataset.acityDialogReady;
      if (mobileDialogKeyHandler) {
        document.removeEventListener('keydown', mobileDialogKeyHandler, true);
        mobileDialogKeyHandler = null;
      }
      uninertSiblings();
      if (prevActiveElement && typeof prevActiveElement.focus === 'function') {
        try { prevActiveElement.focus({ preventScroll: true }); } catch (e) {
          try { prevActiveElement.focus(); } catch (e2) {}
        }
      }
      prevActiveElement = null;
    }

    // Expose teardown so the close path in scripts.js / the
    // syncSearchFormWithLayer mirror below can call it without
    // re-implementing focus restoration.
    window.ac_teardownDoofinderDialog = teardownMobileDialogA11y;

    /* -- 4. Wait for the layer to mount, then run all jobs ----------- */

    function onLayerReady($layer) {
      syncLayerTop();
      // Catch the case where the user typed before the layer mounted —
      // push the current header-input value into DooFinder's input so it
      // jumps straight to the matching result set instead of pre-search.
      // Find the first input with a value (desktop OR mobile copy)
      // and seed DooFinder's hidden input with it.
      const seed = $headerSearch.toArray().map(function (el) { return el.value; }).find(function (v) { return !!v; });
      if (seed) mirrorSearchInput(seed);
      injectCategoriesInto($layer);
      injectMobileCarouselArrows($layer);
      injectMobileSmartFilterCarousels($layer);
      // A11y wiring (mobile-only). Idempotent — guards on root dataset.
      setupMobileDialogA11y($layer);
      // Initial sizing happens after the next paint so the carousel
      // / arrows / hidden popular-searches title etc. have settled.
      requestAnimationFrame(function () {
        sizeMobileRootToContent($layer);
        requestAnimationFrame(function () {
          sizeMobileRootToContent($layer);
        });
      });
      setTimeout(function () { sizeMobileRootToContent($layer); }, 250);
      // Keep root height in sync with viewport changes (rotate, etc.).
      // NB: onLayerReady runs on every layer mount, so this stacks
      // listeners on re-mount. That predates this change — leaving
      // the existing window-level binding as-is to scope our fix.
      const onResize = function () { sizeMobileRootToContent($layer); };
      window.addEventListener('resize', onResize, { passive: true });
      window.addEventListener('orientationchange', onResize, { passive: true });
      // Also re-measure on visualViewport changes — these fire when
      // the soft keyboard slides up / down, which `resize` does NOT
      // catch reliably on iOS. Without this the layer keeps its
      // pre-keyboard height and the bottom half is hidden under the
      // keyboard. Scoped to the latest mounted layer's `$layer`
      // closure; the previous mount's listener will no-op cleanly
      // once its layer node is detached (`tabs` lookup returns null
      // and sizeMobileRootToContent early-returns).
      //
      // Resize only — `scroll` on visualViewport fires on every layout
      // shift, and `sizeMobileRootToContent` writes to the DOM, which
      // is itself a layout shift, which fires another scroll, looping
      // the layer's measure-write-measure cycle as the user types.
      if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', onResize, { passive: true });
      }
      // Convert the raw mobile DooFinder cards (no wishlist / compare /
      // multibuy / Add-to-basket buttons) into the same .ac-doofinder-card
      // template the desktop layer ships, so the existing hydration
      // pipeline picks them up below.
      transformMobileRecommendedCards($layer);
      transformMobileResultsCards($layer);
      hydrateCards($layer.get(0));
      observeResults($layer.get(0));
    }

    /* -- Mobile recommended cards: rebuild into product-card template -*
     *
     * DooFinder's mobile build of the layer doesn't apply the custom
     * card template (configured in the DooFinder cloud admin) to the
     * Recommended Products row — it ships raw `.dfd-card` markup
     * containing only image / title / description / price / "View
     * more →" link. The desktop build uses the full .ac-doofinder-card
     * template with wishlist, compare, multibuy, share, View Product
     * and Add-to-basket controls.
     *
     * To get parity, we walk every `.dfd-card-live` under
     * #df-recommended-products on mobile and rebuild its inner HTML
     * into the .ac-doofinder-card structure. We then strip the
     * data-acdf-hydrated marker so hydrateCards() picks it up next
     * pass and fills the wishlist placeholder. dataset.acityRebuilt
     * marks the wrapper so we don't rebuild on subsequent observer
     * passes (the rebuild is destructive — running it twice would
     * wipe newly-hydrated wishlist HTML).
     */
    // Shared inner rebuild — given a `.dfd-card-live` wrapper, swaps its
    // child `.dfd-card` for the full `.ac-doofinder-card` template. Used
    // by both `transformMobileRecommendedCards` (initial state,
    // #df-recommended-products) and `transformMobileResultsCards`
    // (results state, .dfd-results-grid inside .dfd-content) so the
    // results cards visually match the rest of the site.
    function rebuildMobileCardLive(live) {
      live.setAttribute('data-acity-rebuilt', '1');
      // Tell Phoenix LiveView not to morph the children of this element
      // on subsequent stream patches. Without this, LiveView's diff
      // sees our injected `.ac-doofinder-card` markup, treats it as
      // stale against the server-rendered `.dfd-card`, and overwrites
      // our work — which we then re-detect on the next observer tick
      // and rebuild again. That feedback loop manifests as
      // `.dfd-content` "constantly updating in the DOM" and was the
      // visible flicker the user reported. `phx-update="ignore"` is
      // checked by morphdom on every diff pass, so adding it
      // client-side is enough to break the loop. Stream insert/remove
      // still works at the grid level (this attribute scopes only to
      // inner children of the card itself).
      //
      // LiveView requires `phx-update="ignore"` containers to carry a
      // unique `id`. Stream items already have `data-phx-id` but not
      // necessarily an `id` attribute, so promote `data-phx-id` (or
      // synthesise one from the product id) to satisfy the contract.
      if (!live.id) {
        const phxId = live.getAttribute('data-phx-id') || '';
        const productId = live.getAttribute('data-product-id') || '';
        live.id = phxId || ('acity-card-' + (productId || Math.random().toString(36).slice(2)));
      }
      live.setAttribute('phx-update', 'ignore');

      const productId = live.getAttribute('data-product-id') || '';
      const inner = live.querySelector('.dfd-card');
      if (!inner) return;

      const link = inner.getAttribute('dfd-value-link') || '#';
      const dfid = inner.getAttribute('dfd-value-dfid') || '';
      const cardLinkId = inner.getAttribute('data-card-link-id') || '';
      const imgEl = inner.querySelector('.dfd-card-thumbnail img');
      const imgSrc = imgEl ? imgEl.getAttribute('src') : '';
      const imgAlt = imgEl ? (imgEl.getAttribute('alt') || '') : '';
      const titleEl = inner.querySelector('.dfd-card-title');
      const title = titleEl ? titleEl.textContent.trim() : '';
      const priceEl = inner.querySelector('.dfd-card-price');
      const price = priceEl ? priceEl.textContent.trim() : '';
      const oosEl = inner.querySelector('.dfd-card-flag[data-availability="out-of-stock"]');
      const stockStatus = oosEl ? 'outofstock' : 'instock';

      const html =
        '<div class="ac-doofinder-card" id="' + (inner.id || ('df-result-product-' + productId)) +
        '" dfd-value-dfid="' + escapeAttr(dfid) +
        '" dfd-value-link="' + escapeAttr(link) +
        '" tabindex="0" data-card-link-id="' + escapeAttr(cardLinkId) +
        '" dfd-hook="CardTabNavigation">' +
          '<div class="ac-doofinder-card__inner">' +
            '<div class="ac-doofinder-card__body">' +
              '<div class="ac-doofinder-card__media">' +
                '<a href="' + escapeAttr(link) + '" class="ac-doofinder-card__image-link"' +
                  (cardLinkId ? ' id="' + escapeAttr(cardLinkId) + '"' : '') + '>' +
                  (imgSrc ? '<img class="ac-doofinder-card__image" src="' + escapeAttr(imgSrc) + '" alt="' + escapeAttr(imgAlt) + '">' : '') +
                '</a>' +
              '</div>' +
              '<a href="' + escapeAttr(link) + '" class="ac-doofinder-card__title-link">' +
                '<h2 class="ac-doofinder-card__title">' + escapeText(title) + '</h2>' +
              '</a>' +
              '<a href="' + escapeAttr(link) + '" class="ac-doofinder-card__pricing">' +
                '<span class="ac-doofinder-card__price">' + escapeText(price) + '</span>' +
              '</a>' +
              '<div class="ac-doofinder-card__actions">' +
                '<div class="ac-doofinder-card__share">' +
                  '<button type="button" class="share-btn ac-doofinder-card__share-btn" data-share-url="' + escapeAttr(link) + '" data-share-title="' + escapeAttr(title) + '">' +
                    '<i class="fa-regular fa-share-nodes"></i>' +
                  '</button>' +
                '</div>' +
                '<div class="compare-container ac-doofinder-card__compare">' +
                  '<input type="checkbox" id="compare-' + escapeAttr(dfid || productId) + '" name="compare" value="' + escapeAttr(productId) + '">' +
                  '<label for="compare-' + escapeAttr(dfid || productId) + '">Compare</label>' +
                '</div>' +
                '<button class="multi-buy-btn ac-doofinder-card__multibuy" data-product-id="' + escapeAttr(productId) + '">' +
                  '<i class="fa-regular fa-circle-plus ac-doofinder-card__multibuy-icon"></i>' +
                  '<span class="ac-doofinder-card__multibuy-label">Multibuy</span>' +
                  '<span class="multi-qty ac-doofinder-card__multibuy-qty">0</span>' +
                '</button>' +
              '</div>' +
              '<div class="ac-doofinder-card__cta-row ">' +
                '<a href="' + escapeAttr(link) + '" class="ac-doofinder-card__view">View Product</a>' +
                '<button type="button" class="add-to-cart-btn ac-doofinder-card__buy ac-doofinder-card__buy--general" data-product-id="' + escapeAttr(productId) + '" data-stock-status="' + escapeAttr(stockStatus) + '">' +
                  '<span>Add To Basket</span>' +
                '</button>' +
              '</div>' +
            '</div>' +
          '</div>' +
        '</div>';

      live.innerHTML = html;
    }

    function transformMobileRecommendedCards($layer) {
      // Disabled: product cards are fully hidden in the mobile layer
      // (client brief Jul 2026: keyword pills only, no products) — the
      // rebuild would churn DOM for cards nobody can see. Function kept
      // so the brief can be reversed by deleting this return.
      return; // eslint-disable-line no-unreachable

      const layerEl = $layer && $layer.length ? $layer.get(0) : null;
      if (!layerEl) return;
      const root = layerEl.closest('.dfd-root');
      if (!root || !root.classList.contains('dfd-mobile')) return;

      const wrap = layerEl.querySelector('#df-recommended-products');
      if (!wrap) return;

      const lives = wrap.querySelectorAll('.dfd-card-live:not([data-acity-rebuilt])');
      if (!lives.length) return;
      lives.forEach(rebuildMobileCardLive);
    }

    // Results-grid card rebuild — same template as the recommended row,
    // but scoped to the `.dfd-results-grid` blocks inside `.dfd-content`
    // that DooFinder fills with stream results once the user types.
    // Excludes `#df-recommended-products`'s nested grid so we don't
    // double-rebuild cards already handled by transformMobileRecommendedCards.
    function transformMobileResultsCards($layer) {
      // Disabled: see transformMobileRecommendedCards. Not stamping
      // `data-acity-results-carousel` / injecting the arrow wrapper any
      // more also removes the display:flex rules that were outranking
      // the mobile product-hide CSS.
      return; // eslint-disable-line no-unreachable

      const layerEl = $layer && $layer.length ? $layer.get(0) : null;
      if (!layerEl) return;
      const root = layerEl.closest('.dfd-root');
      if (!root || !root.classList.contains('dfd-mobile')) return;

      const grids = layerEl.querySelectorAll('.dfd-content .dfd-results-grid');
      if (!grids.length) return;

      grids.forEach(function (grid) {
        if (grid.closest('#df-recommended-products')) return;
        // Re-wrap whenever LiveView has reverted our previous wrap.
        // Source of truth is parent-element identity, NOT a data
        // attribute — morphdom typically preserves unknown attrs like
        // `data-acity-results-carousel` on the grid even when it
        // morphs the grid's parent (.dfd-content) and strips out the
        // `.acity-results-carousel` wrapper we added. The data attr
        // alone made the early return short-circuit on every pass
        // after the first revert, and the user never saw the carousel
        // again. Checking the live parent ensures we re-inject.
        grid.setAttribute('data-acity-results-carousel', '1');
        if (!grid.parentElement || !grid.parentElement.classList.contains('acity-results-carousel')) {
          injectResultsGridArrows(grid);
        }
        const lives = grid.querySelectorAll('.dfd-card-live:not([data-acity-rebuilt])');
        lives.forEach(rebuildMobileCardLive);
      });
    }

    // Prev/next arrows for results-grid carousel — same UX as
    // injectMobileCarouselArrows but the wrapper here is the grid
    // itself, so we wrap it in a flex container at injection time.
    function injectResultsGridArrows(grid) {
      // Already wrapped on a previous pass? Bail.
      if (grid.parentElement && grid.parentElement.classList.contains('acity-results-carousel')) {
        return;
      }

      const wrap = document.createElement('div');
      wrap.className = 'acity-results-carousel';

      const prev = document.createElement('button');
      prev.type = 'button';
      prev.className = 'acity-carousel-arrow acity-carousel-arrow--prev';
      prev.setAttribute('aria-label', 'Scroll results left');
      prev.innerHTML = '<i class="fa-regular fa-chevron-left" aria-hidden="true"></i>';

      const next = document.createElement('button');
      next.type = 'button';
      next.className = 'acity-carousel-arrow acity-carousel-arrow--next';
      next.setAttribute('aria-label', 'Scroll results right');
      next.innerHTML = '<i class="fa-regular fa-chevron-right" aria-hidden="true"></i>';

      grid.parentNode.insertBefore(wrap, grid);
      wrap.appendChild(prev);
      wrap.appendChild(grid);
      wrap.appendChild(next);

      function scrollByCard(direction) {
        const firstCard = grid.querySelector('.dfd-card-live, .ac-doofinder-card, .dfd-card');
        const step = firstCard ? firstCard.getBoundingClientRect().width + 16 : grid.clientWidth * 0.8;
        grid.scrollBy({ left: direction * step, behavior: 'smooth' });
      }
      prev.addEventListener('click', function () { scrollByCard(-1); });
      next.addEventListener('click', function () { scrollByCard(1); });
    }

    function escapeAttr(s) {
      return String(s == null ? '' : s)
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
    }

    function escapeText(s) {
      return String(s == null ? '' : s)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
    }

    /* -- Mobile parity: close button + carousel arrows ---------------- *
     *
     * DooFinder's mobile layer is missing two things that exist on
     * desktop:
     *
     *   1. A visible `.dfd-close-button` in the header. Mobile only
     *      ships a back-arrow icon button (`.dfd-btn-icon[dfd-click=
     *      "close-layer"]`) tucked inside the searchbox slot — easy to
     *      miss and inconsistent with desktop. We mirror desktop by
     *      injecting a `.dfd-close-button` clone.
     *
     *   2. Prev/next arrows on the recommended-products row. Mobile
     *      ships a flat `.dfd-results-grid` with no carousel JS at all
     *      (DooFinder relies on native swipe). The CSS we already have
     *      makes it horizontally scrollable, so all we need is two
     *      visible buttons that scroll the container by a card width.
     *
     * Both helpers are idempotent — the dataset flag prevents duplicate
     * injection if the layer subtree mutates (DooFinder re-renders the
     * pre-search panel on input changes).
     */
    // (injectMobileCloseButton removed: the mobile search-btn in the
    // nav row now functions as the close affordance — when the form is
    // open the button shows an X icon + "Close" label. An in-layer X
    // would be redundant, so we no longer inject it.)

    // Size the mobile `.dfd-root` so its height matches the inner
    // `.dfd-tabs` content height + 24px. The flex-column layer (SCSS)
    // makes `.dfd-tabs` collapse to its actual content; we mirror that
    // measurement back onto the root via a CSS custom property so the
    // gradient `::after` sits flush with the bottom of the panel.
    //
    // Runs on:
    //  - initial layer mount (onLayerReady)
    //  - every observed mutation inside the layer (suggestions / cards
    //    can be re-rendered when DooFinder swaps screens)
    //  - viewport resize
    function sizeMobileRootToContent($layer) {
      const layerEl = $layer && $layer.length ? $layer.get(0) : null;
      if (!layerEl) return;
      const root = layerEl.closest('.dfd-root');
      if (!root || !root.classList.contains('dfd-mobile')) return;

      const tabs = layerEl.querySelector('.dfd-tabs');
      if (!tabs) return;

      const offset = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--ac-df-header-offset')) || 64;
      // Prefer `visualViewport.height` over `window.innerHeight` so the
      // available cap shrinks correctly when the soft keyboard opens
      // on iOS / Android — keeps the results panel above the keyboard
      // instead of behind it. Falls back to `innerHeight` when the
      // VisualViewport API is unavailable (older Safari / WebView).
      const viewportH = (window.visualViewport && window.visualViewport.height)
        ? window.visualViewport.height
        : window.innerHeight;
      const cap = viewportH - offset;

      // Product cards never render inside the mobile layer any more
      // (client brief Jul 2026: keyword pills only), so EVERY screen
      // collapses to its content height — the old fill-viewport branch
      // for `mobile-results` would size an empty panel to the whole
      // screen. Pre/post-search panels now measure identically.

      // Clamp root to
      // `.dfd-header` + `.dfd-tabs` content height + 24px. The Cancel
      // button is absolutely positioned inside `.dfd-header`, but the
      // header itself now reserves a 44px min-height so the absolute
      // button doesn't overlap the tabs body — that header height has
      // to be added to the measurement so the gradient `::after` sits
      // flush with the bottom of the panel instead of cutting in 44px
      // early.
      const headerEl = layerEl.querySelector('.dfd-header');
      const headerH = headerEl ? headerEl.getBoundingClientRect().height : 0;
      const tabsH = tabs.getBoundingClientRect().height;
      const target = Math.min(headerH + tabsH + 24, cap);
      root.style.setProperty('--ac-df-mobile-root-h', target + 'px');
    }

    /* -- Smart-filters consolidation (results state) ------------------ *
     *
     * Once the user types, DooFinder swaps `.dfd-content` from the
     * initial-content tree (`.dfd-bc-aside` + Our Favourites) to the
     * results tree, which contains TWO `.dfd-smart-filters` blocks
     * (one for categories, one for brand) above the results grid. On
     * mobile we consolidate them into a SINGLE wrapping pill grid (max
     * 5 visible) so the post-search UI matches the pre-search bc-aside
     * pill grid — no horizontal-scroll carousel, no prev/next chevrons.
     *
     * Strategy:
     *   1. Pick the first `.dfd-smart-filters` as the host. Mark it.
     *   2. Harvest pill children from EVERY `.dfd-smart-filters` block
     *      (host + siblings), skipping their headings.
     *   3. Mark + hide the now-empty sibling `.dfd-smart-filters` blocks.
     *   4. Empty the host and re-append the first 5 pills directly.
     *      `.dfd-smart-filters` already gets `flex-wrap: wrap` from the
     *      SCSS so they lay out as a wrapping grid.
     *
     * Idempotent: the host's `data-acity-smart-carousel` flag short-
     * circuits subsequent passes. If LiveView replaces the filters
     * subtree, the new nodes lack the flag and we rebuild.
     *
     * (Function name and the `-carousel` data attribute kept for
     * compatibility with SCSS selectors that already exist on the
     * processed host. No carousel is actually built.)
     */
    function injectMobileSmartFilterCarousels($layer) {
      const layerEl = $layer && $layer.length ? $layer.get(0) : null;
      if (!layerEl) return;
      const root = layerEl.closest('.dfd-root');
      if (!root || !root.classList.contains('dfd-mobile')) return;

      const filters = layerEl.querySelectorAll('.dfd-smart-filters');
      if (!filters.length) return;

      // If the host already exists and is still in the DOM, nothing to
      // do — the user pills inside it are stable across observer ticks.
      const existingHost = layerEl.querySelector('.dfd-smart-filters[data-acity-smart-carousel="1"]');
      if (existingHost) {
        // Hide any newly-arrived sibling `.dfd-smart-filters` (LiveView
        // can re-render the secondary block on infinite-scroll patches).
        Array.prototype.forEach.call(filters, function (el) {
          if (el !== existingHost && !el.hasAttribute('data-acity-smart-merged')) {
            el.setAttribute('data-acity-smart-merged', '1');
            el.style.display = 'none';
          }
        });
        return;
      }

      const host = filters[0];
      host.setAttribute('data-acity-smart-carousel', '1');

      // Collect every pill across every .dfd-smart-filters block. The
      // pill row inside each block is whatever children DooFinder
      // emits — usually `<button dfd-click="apply-filter">` or
      // wrapper divs. We strip any headings (`.dfd-title-sm` /
      // anything with "title" in its class) before harvesting.
      const allPills = [];
      Array.prototype.forEach.call(filters, function (filter) {
        const heading = filter.querySelector(':scope > .dfd-title-sm, :scope > [class*="title"]');
        Array.prototype.forEach.call(filter.children, function (child) {
          if (child === heading) return;
          allPills.push(child);
        });
        if (filter !== host) {
          filter.setAttribute('data-acity-smart-merged', '1');
          filter.style.display = 'none';
        }
      });

      if (!allPills.length) return;

      // Cap at the first 5 pills per client brief — matches the
      // pre-search bc-aside pill cap. The remaining pills become
      // orphans (host's own) or stay hidden in their original (now
      // `display: none`) parent (siblings); either way they don't
      // render.
      const visible = allPills.slice(0, 5);

      // Empty the host (its original children are in allPills already
      // — removing them here just detaches them from the DOM if they
      // weren't selected to remain visible), then re-attach the
      // visible pills directly. The flex-wrap-row rule on
      // `.dfd-smart-filters` handles the grid layout.
      while (host.firstChild) host.removeChild(host.firstChild);
      visible.forEach(function (el) { host.appendChild(el); });
    }

    function injectMobileCarouselArrows($layer) {
      // Disabled: see transformMobileRecommendedCards — the recommended
      // products row the arrows drove is permanently hidden on mobile.
      return; // eslint-disable-line no-unreachable

      const layerEl = $layer && $layer.length ? $layer.get(0) : null;
      if (!layerEl) return;
      const root = layerEl.closest('.dfd-root');
      if (!root || !root.classList.contains('dfd-mobile')) return;

      const wrap = layerEl.querySelector('#df-recommended-products');
      if (!wrap || wrap.dataset.acityArrows === '1') return;

      const track = wrap.querySelector('.dfd-results-grid');
      if (!track) return;

      wrap.dataset.acityArrows = '1';

      // Mark the wrapper so positioning rules in _doofinder.scss can
      // target the carousel-arrow buttons we add as children.
      wrap.classList.add('acity-mobile-carousel');

      const prev = document.createElement('button');
      prev.type = 'button';
      prev.className = 'acity-carousel-arrow acity-carousel-arrow--prev';
      prev.setAttribute('aria-label', 'Scroll left');
      prev.innerHTML = '<i class="fa-regular fa-chevron-left" aria-hidden="true"></i>';

      const next = document.createElement('button');
      next.type = 'button';
      next.className = 'acity-carousel-arrow acity-carousel-arrow--next';
      next.setAttribute('aria-label', 'Scroll right');
      next.innerHTML = '<i class="fa-regular fa-chevron-right" aria-hidden="true"></i>';

      function scrollByCard(direction) {
        const firstCard = track.querySelector('.dfd-card-live, .ac-doofinder-card, .dfd-card');
        const step = firstCard ? firstCard.getBoundingClientRect().width + 16 : track.clientWidth * 0.8;
        track.scrollBy({ left: direction * step, behavior: 'smooth' });
      }

      prev.addEventListener('click', function () { scrollByCard(-1); });
      next.addEventListener('click', function () { scrollByCard(1); });

      // Insert as flex siblings of .dfd-results-grid: prev → track → next.
      // CSS turns the wrapper into a flex row so the arrows sit either
      // side of the carousel content rather than overlapping it.
      wrap.insertBefore(prev, wrap.firstChild);
      wrap.appendChild(next);

      // iOS Safari deferred-paint workaround. After we mutate the
      // wrapper from a block to a flex row, Safari sometimes leaves
      // the cards invisible until the user scrolls. Reading
      // offsetHeight forces a synchronous layout/paint pass; the
      // double rAF puts the read AFTER the next paint frame, which
      // is the only timing that reliably wakes Safari up across
      // every iOS version we've tested. Cheap (it only runs once
      // per layer mount because of the dataset guard at the top of
      // this function).
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          wrap.style.transform = 'translateZ(0)';
          // Force layout read.
          /* eslint-disable no-unused-expressions */
          wrap.offsetHeight;
          track.offsetHeight;
          /* eslint-enable no-unused-expressions */
        });
      });
    }

    // Observe the layer's results region so cards added after the user
    // starts typing get hydrated too. DooFinder doesn't expose a stable
    // results-container class, so we observe the whole layer subtree and
    // throttle hydration runs to coalesce burst mutations.
    const observedLayers = new WeakSet();
    let resultsHydrateTimer = null;
    let resultsHydrateBurstStart = 0;

    // Quiet-window debounce of 250ms — DooFinder does a lot of micro-
    // mutations (badge updates, focus rings, click analytics) and
    // coalescing them into a single hydrate pass keeps the observer from
    // running hot on burst mutations. BUT: the mobile layer's
    // `.dfd-content` is patched by the Phoenix LiveView stream as the
    // user scrolls and as the initial 300+ product results arrive — a
    // pure clearTimeout/setTimeout debounce never fires because each
    // patch arrives <250ms apart, so our close-button / carousel
    // injectors never run and the button visually flickers off then
    // disappears entirely. Add a max-wait of 750ms so the callback
    // fires periodically even under sustained mutations.
    const HYDRATE_QUIET_WAIT = 250;
    const HYDRATE_MAX_WAIT   = 750;

    function observeResults(layerEl) {
      if (!layerEl || observedLayers.has(layerEl)) return;
      observedLayers.add(layerEl);

      const $layer = $(layerEl);

      function runHydratePass() {
        resultsHydrateTimer = null;
        resultsHydrateBurstStart = 0;
        hydrateCards(layerEl);
        // bc-aside is part of DooFinder's initial-content tree, which
        // gets re-rendered when query state changes. Re-inject is
        // idempotent (short-circuits if our list still exists).
        //
        // Suggestion buttons themselves are handled by a delegated
        // capture-phase click listener (see `bc-aside button →
        // shop URL` block above) — no DOM rewrite needed, so no
        // observer-tick call is required here.
        injectCategoriesInto($layer);

        // Mobile re-renders the pre-search panel when DooFinder
        // hot-swaps state, which can wipe our injected close button
        // / arrows / rebuilt cards. All three injectors are
        // idempotent (dataset / DOM existence guards), so retrying
        // on every settled mutation costs nothing and keeps them in
        // place. The card-rebuild MUST run before hydrateCards()
        // below, hence the explicit call here ahead of the trailing
        // hydrateCards inside the same throttled tick.
        injectMobileCarouselArrows($layer);
        injectMobileSmartFilterCarousels($layer);
        transformMobileRecommendedCards($layer);
        transformMobileResultsCards($layer);
        // Re-measure on every mutation pass — DooFinder reflows the
        // content panel when state changes (e.g. typing starts).
        sizeMobileRootToContent($layer);
      }

      const resultsObserver = new MutationObserver(function () {
        const now = Date.now();
        if (!resultsHydrateBurstStart) resultsHydrateBurstStart = now;
        if (resultsHydrateTimer) clearTimeout(resultsHydrateTimer);

        // Wait at most MAX_WAIT from the first mutation of this burst,
        // and otherwise wait for QUIET_WAIT of inactivity. Clamping to
        // 0 forces an immediate run when MAX_WAIT has already elapsed.
        const elapsed = now - resultsHydrateBurstStart;
        const wait = Math.max(0, Math.min(HYDRATE_QUIET_WAIT, HYDRATE_MAX_WAIT - elapsed));
        resultsHydrateTimer = setTimeout(runHydratePass, wait);
      });

      resultsObserver.observe(layerEl, {
        childList: true,
        subtree: true,
      });

      // Separately watch `data-dfd-screen` on the root — DooFinder
      // mutates it when the user starts/stops typing (mobile-initial
      // ↔ mobile-results / mobile-brand-categories etc.) and that
      // attribute alone is enough to know we need to re-measure.
      const root = layerEl.closest('.dfd-root');
      if (root) {
        const screenObserver = new MutationObserver(function () {
          sizeMobileRootToContent($(layerEl));
        });
        screenObserver.observe(root, {
          attributes: true,
          attributeFilter: ['data-dfd-screen'],
        });
      }
    }

    // If it's already in the DOM (e.g. user toggled layer before this
    // script ran), handle immediately.
    const $existing = $(LAYER_SELECTOR);
    if ($existing.length) {
      onLayerReady($existing);
    }

    // Watch <body> for the layer being inserted. DooFinder re-mounts the
    // layer on every open, so we keep the observer alive for the lifetime
    // of the page rather than disconnecting it.
    const observer = new MutationObserver(function (mutations) {
      for (let i = 0; i < mutations.length; i++) {
        const added = mutations[i].addedNodes;
        for (let j = 0; j < added.length; j++) {
          const node = added[j];
          if (node.nodeType !== 1) continue; // elements only

          const $node = $(node);
          const $layer = $node.is(LAYER_SELECTOR)
            ? $node
            : $node.find(LAYER_SELECTOR);

          if ($layer.length) {
            onLayerReady($layer);
          }
        }
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  },
};

// Exported for tests / future inter-module use; harmless side-effect free.
module.exports.HYDRATION_ENDPOINTS = HYDRATION_ENDPOINTS;
